<template>
  <div class="bg-white shadow-md rounded-xl p-4 m-4">
    <MonkeyLoader v-if="isLoading" />
    <h2 class="text-xl font-semibold mb-4 text-gray-800">
      SPK Subcon Entry
    </h2>

    <form @submit.prevent="saveForm">
      <div class="grid grid-cols-1 gap-4">
        <div class="form-section">
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="subcon">Sub Con:</label>
            <select class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="subcon" v-model="formData.subcon" required>
              <option value="">-- Pilih Subcon --</option>
              <option v-for="subconOption in subconOptions" :key="subconOption.id" :value="subconOption.id">{{
                subconOption.entityName }}</option>
            </select>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="subconContactName">Contact Name:</label>
            <select class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="subconContactName" v-model="formData.subconContactName" @change="updateSubconInfo" required>
              <option value="">-- Pilih Contact --</option>
              <option v-for="contactOption in contactOptions" :key="contactOption.id" :value="contactOption.id">{{
                contactOption.contactName }}</option>
            </select>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="subconJabatan">Job Title:</label>
            <input class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
              type="text" id="subconJabatan" v-model="subconInfo.jabatan" disabled />
          </div>
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="subconHp">No.Hp:</label>
            <input class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
              type="text" id="subconHp" v-model="subconInfo.noHp" disabled />
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="pekerjaan">Job:</label>
            <div class="flex items-center gap-2">
              <select class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                id="pekerjaan" v-model="formData.pekerjaan" required>
                <option value="">-- Pilih Pekerjaan --</option>
                <option v-for="job in jobOptions" :key="job.id" :value="job.id">{{ job.jobname }}</option>
              </select>
              <button type="button" @click="openJobModal"
                class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                +
              </button>
            </div>
          </div>

          <div class="mb-4 flex gap-4">
            <div class="w-2/3">
              <label class="block text-gray-700 text-sm font-bold mb-2" for="spkNumber">SPK #:</label>
              <input
                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
                type="text" id="spkNumber" v-model="formData.spkNumber" disabled />
            </div>
            <div class="w-1/3">
              <label class="block text-gray-700 text-sm font-bold mb-2" for="spkDate">SPK Date:</label>
              <input class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                type="date" id="spkDate" v-model="formData.spkDate" />
            </div>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="remark">Remark:</label>
            <textarea class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
              id="remark" rows="3" v-model="formData.remark" required></textarea>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="poNumber">PO #:</label>
            <v-select id="poNumber" :options="poOptions" :reduce="po => po.id" label="poNumber"
              v-model="formData.poNumber" placeholder="-- Ketik atau Pilih PO # --" :filterable="true"
              class="input-border shadow appearance-none border rounded w-full text-gray-700" required>
              <template #option="option">
                {{ option.poNumber }} ({{ option.siteNames }})
              </template>
              <template #selected-option="option">
                {{ option.poNumber }} ({{ option.siteNames }})
              </template>
            </v-select>
          </div>
          <div class="mb-4 flex items-center">
            <div class="w-5/6">
              <label class="block text-gray-700 text-sm font-bold mb-2" for="siteName">Site Name:</label>
              <select class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                id="siteName" v-model="formData.siteName">
                <option value="">-- Pilih Site --</option>
                <option v-for="site in siteOptions" :key="site.id" :value="site.id">{{ site.name }}</option>
              </select>
            </div>
            <div class="w-1/6 pl-4 flex justify-end">
              <button type="button" @click="addRowToDetailPekerjaan"
                class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded">
                Add To List
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="data-grid-container mt-6">
        <div class="overflow-x-auto">
          <table class="min-w-full table-auto">
            <thead>
              <tr>
                <th class="px-4 py-2 border-b text-left">Dlt.</th>
                <th class="px-4 py-2 border-b text-left">No</th>
                <th class="px-4 py-2 border-b text-left">PO Number</th>
                <th class="px-4 py-2 border-b text-left">Sitename</th>
                <th class="px-4 py-2 border-b text-left">Area</th>
                <th class="px-4 py-2 border-b text-left">Project Name</th>
                <th class="px-4 py-2 border-b text-right">Price</th>
                <th class="px-4 py-2 border-b text-left">Start Date</th>
                <th class="px-4 py-2 border-b text-left">End Date</th>
                <th class="px-4 py-2 border-b text-left hidden">PO ID</th>
                <th class="px-4 py-2 border-b text-left hidden">Site ID</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(detail, index) in detailPekerjaan" :key="index">
                <td class="px-4 py-2 border-b">
                  <button type="button" @click="deleteDetailRow(index)"
                    class="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded-full text-xs">X</button>
                </td>
                <td class="px-4 py-2 border-b">{{ index + 1 }}</td>
                <td class="px-4 py-2 border-b">{{ detail.poNumber }}</td>
                <td class="px-4 py-2 border-b">
                  <input type="text" v-model="detail.sitename"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700">
                </td>
                <td class="px-4 py-2 border-b"><input type="text" v-model="detail.area"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
                    readonly>
                </td>
                <td class="px-4 py-2 border-b"><input type="text" v-model="detail.projectName"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
                    readonly>
                </td>
                <td class="px-4 py-2 border-b text-right">
                  <input type="text" v-model="detail.priceFormatted" @input="updatePrice(detail)"
                    @blur="formatPriceOnBlur(detail)"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 text-right">
                </td>
                <td class="px-4 py-2 border-b"><input type="date" v-model="detail.startDate"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"></td>
                <td class="px-4 py-2 border-b"><input type="date" v-model="detail.endDate"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"></td>
                <td class="px-4 py-2 border-b hidden">{{ detail.poId }}</td>
                <td class="px-4 py-2 border-b hidden">{{ detail.siteId }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="gspe-info mt-6 border rounded p-4">
        <h3 class="text-lg font-semibold mb-2 text-gray-800">GSPE</h3>
        <div class="mb-4">
          <label class="block text-gray-700 text-sm font-bold mb-2" for="gspeNama">Name:</label>
          <select class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
            id="gspeNama" v-model="formData.gspeNama" required>
            <option value="">-- Pilih Nama --</option>
            <option v-for="user in gspeNameOptions" :key="user.id" :value="user.id">{{ user.username }}</option>
          </select>
        </div>
        <div class="mb-4">
          <label class="block text-gray-700 text-sm font-bold mb-2" for="gspeJabatan">Job Title:</label>
          <input class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
            type="text" id="gspeJabatan" v-model="formData.gspeJabatan" disabled />
        </div>
      </div>
      <div class="flex justify-center mt-4 gap-2">
        <button type="submit"
          class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-6 rounded w-1/6">Save</button>
        <button type="button" @click="exitForm"
          class="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-6 rounded w-1/6">Exit</button>
      </div>
    </form>

    <div v-if="isJobModalVisible" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white p-6 rounded-lg shadow-xl w-1/3">
        <h3 class="text-lg font-bold mb-4">Add New Job</h3>
        <div class="mb-4">
          <label for="newJobName" class="block text-sm font-medium text-gray-700">Job Name:</label>
          <input type="text" id="newJobName" v-model="newJobName"
            class="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="e.g., Installasi FO" @keyup.enter="handleSaveNewJob" required>
        </div>
        <div class="flex justify-end gap-2">
          <button @click="closeJobModal" type="button"
            class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded">
            Cancel
          </button>
          <button @click="handleSaveNewJob" type="button" :disabled="isSavingJob"
            class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
            :class="{ 'opacity-50 cursor-not-allowed': isSavingJob }">
            <span v-if="isSavingJob">Saving...</span>
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import vSelect from 'vue-select'; // Import vue-select
import 'vue-select/dist/vue-select.css'; // Import CSS for vue-select
import MonkeyLoader from '../Layout/MonkeyLoader.vue';

const API_BASE_URL = `${window.location.origin}`;
const API_ENDPOINTS = {
  subconOptions: `${API_BASE_URL}/api/entities/type4`,
  contacts: (entityId) => `${API_BASE_URL}/api/contacts/${entityId}`,
  lastSpk: `${API_BASE_URL}/api/spksubconfiles/lastnourut`,
  poOptions: 'https://crm-local.iotech.my.id/po/subcon?bpo=true',
  projectByPo: (poId) => `https://meeting-backend.iotech.my.id/projects-bypo/${poId}`,
  gspeUsers: 'https://meeting-backend.iotech.my.id/ext-users',
  validateOutstandingSpk: (poId) => `https://logistic-app.iotech.my.id/api/dn/all?hasSpk=true&poId=${poId}`,
  saveSpk: `${API_BASE_URL}/api/spksubconfiles`,
  jobOptions: `${API_BASE_URL}/api/jobfiles`,
  saveJob: `${API_BASE_URL}/api/jobfiles`,
};

const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

const getInitialFormData = () => ({
  subcon: '',
  subconContactName: '',
  pekerjaan: '',
  spkNumber: '',
  spkDate: getCurrentDate(),
  remark: '',
  poNumber: null,
  siteName: '',
  gspeNama: '',
  gspeJabatan: '',
});

export default {
  components: {
    vSelect, MonkeyLoader
  },
  data() {
    return {
      formData: getInitialFormData(),
      subconInfo: { jabatan: '', noHp: '' },
      detailPekerjaan: [],
      selectedProjectData: null,
      lastSpkNumber: 0,
      subconOptions: [],
      contactOptions: [],
      poOptions: [],
      siteOptions: [],
      gspeNameOptions: [],
      jobOptions: [],
      isFormValid: true,
      isLoading: false,
      isJobModalVisible: false,
      newJobName: '',
      isSavingJob: false,
    };
  },

  methods: {
    async fetchData(url, targetState, mapper = null) {
      try {
        this.isLoading = true;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        let data = response.data;
        if (mapper) {
          data = mapper(data);
        }
        this[targetState] = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error(`Error fetching ${targetState}:`, error);
        this[targetState] = [];
      } finally {
        this.isLoading = false;
      }
    },

    loadSubconOptions() { this.fetchData(API_ENDPOINTS.subconOptions, 'subconOptions'); },
    loadContactOptions(entityId) {
      if (!entityId) { this.contactOptions = []; return; }
      this.fetchData(API_ENDPOINTS.contacts(entityId), 'contactOptions');
    },
    loadPoOptions() {
      const mapper = (data) => data.map(po => ({
        id: po.id,
        poNumber: po.poNumber,
        sites: po.sites,
        siteNames: po.sites.map(site => site.name).join(', '),
        rfsList: po.rfsList,
      })).sort((a, b) => a.poNumber.localeCompare(b.poNumber));
      this.fetchData(API_ENDPOINTS.poOptions, 'poOptions', mapper);
    },
    loadGspeUsers() {
      const mapper = (data) => data
        .filter(user => ['Admin Project', 'Purchasing'].includes(user.departmentName))
        .map(user => ({
          id: user.id,
          username: user.name,
          authorizationLevelName: user.authorizationLevelName,
        }))
        .sort((a, b) => a.username.localeCompare(b.username));
      this.fetchData(API_ENDPOINTS.gspeUsers, 'gspeNameOptions', mapper);
    },
    loadJobOptions() {
      this.fetchData(API_ENDPOINTS.jobOptions, 'jobOptions');
    },
    async loadLastSpkNumber() {
      try {
        const response = await axios.get(API_ENDPOINTS.lastSpk, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        this.lastSpkNumber = Number(response.data.lastNoUrut) || 0;
      } catch (error) {
        console.error('Error fetching last SPK number:', error);
        this.lastSpkNumber = 0;
      }
      this.formData.spkNumber = this.generateSpkNumber();
    },
    generateSpkNumber() {
      const nextNumber = String(this.lastSpkNumber + 1).padStart(5, '0');
      const monthRoman = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'][new Date().getMonth()];
      const year = new Date().getFullYear();
      return `${nextNumber}/SPK/PROJ/GSPE/${monthRoman}/${year}`;
    },

    addRowToDetailPekerjaan() {
      const currentPoId = this.formData.poNumber;
      if (!currentPoId) {
        Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Harap pilih PO # terlebih dahulu.' });
        return;
      }
      const selectedPo = this.poOptions.find(po => po.id == currentPoId);
      if (!selectedPo) {
        console.error('Data PO tidak ditemukan untuk ID:', currentPoId);
        Swal.fire({ icon: 'error', title: 'Error', text: 'Data PO yang dipilih tidak valid.' });
        return;
      }

      const poNumberToAdd = selectedPo.poNumber;
      const selectedSite = this.siteOptions.find(site => site.id == this.formData.siteName);
      const siteIdForCheck = selectedSite ? selectedSite.id : '0';
      const siteNameToAdd = selectedSite ? selectedSite.name : '';

      if (siteIdForCheck !== '0') {
        const isDuplicate = this.detailPekerjaan.some(row =>
          row.poId == currentPoId && row.siteId == siteIdForCheck
        );
        if (isDuplicate) {
          Swal.fire({ icon: 'warning', title: 'Duplikat', text: `Site "${siteNameToAdd}" dari PO yang sama sudah ada di dalam tabel.` });
          return;
        }
      } else {
        if (!siteNameToAdd.trim()) {
          Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Untuk site "OTHER", harap isi nama site secara manual di tabel.' });
        }
      }

      this.detailPekerjaan.push({
        poNumber: poNumberToAdd,
        poId: currentPoId,
        siteId: siteIdForCheck,
        sitename: siteNameToAdd,
        area: this.selectedProjectData?.city?.name || '',
        projectName: this.selectedProjectData?.name || '',
        price: 0,
        priceFormatted: '0',
        startDate: getCurrentDate(),
        endDate: getCurrentDate(),
        balanceinv: 0,
      });
      this.formData.siteName = '';
    },
    deleteDetailRow(index) { this.detailPekerjaan.splice(index, 1); },

    updateSubconInfo() {
      const contact = this.contactOptions.find(c => c.id == this.formData.subconContactName);
      this.subconInfo = { jabatan: contact?.contactjt || '', noHp: contact?.contactphone || '' };
    },
    updatePrice(detail) {
      const cleanedValue = detail.priceFormatted.replace(/\D/g, '');
      detail.price = Number(cleanedValue);
      detail.priceFormatted = new Intl.NumberFormat('id-ID').format(detail.price || 0);
    },
    formatPriceOnBlur(detail) { detail.priceFormatted = new Intl.NumberFormat('id-ID').format(detail.price || 0); },

    async saveForm() {
      if (this.detailPekerjaan.length === 0) {
        Swal.fire({ icon: 'warning', title: 'Data Tidak Lengkap', text: 'Harap tambahkan setidaknya satu baris ke Detail Pekerjaan.' });
        return;
      }

      for (const [index, detail] of this.detailPekerjaan.entries()) {
        if (!detail.sitename || detail.sitename.trim() === '') {
          Swal.fire({ icon: 'warning', title: 'Data Tidak Lengkap', text: `Harap isi Sitename pada baris #${index + 1}.` });
          return;
        }
        if (!detail.price || detail.price <= 0) {
          Swal.fire({ icon: 'warning', title: 'Data Tidak Valid', text: `Harap isi Price lebih dari 0 pada baris #${index + 1}.` });
          return;
        }
      }

      for (const detail of this.detailPekerjaan) {
        const poData = this.poOptions.find(po => po.id == detail.poId);
        if (poData && (!poData.rfsList || poData.rfsList.length === 0)) {
          Swal.fire({ icon: 'error', title: 'Validasi Gagal', text: `Site "${detail.sitename}" ini RFS nya belum ada.` });
          return;
        }
      }

      try {
        for (const detail of this.detailPekerjaan) {
          const validationUrl = API_ENDPOINTS.validateOutstandingSpk(detail.poId);
          const response = await axios.get(validationUrl, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          if (response.data && Array.isArray(response.data) && response.data.length > 0) {
            const hasOutstandingSpk = response.data.some(dn => dn.spkId === null || dn.spkId === undefined);
            if (hasOutstandingSpk) {
              Swal.fire({ icon: 'error', title: 'Validasi Gagal', text: `Site "${detail.sitename}" ini SPK nya masih Outstanding.` });
              return;
            }
          }
        }
      } catch (validationError) {
        console.error('Error during outstanding SPK validation:', validationError);
        Swal.fire({ icon: 'error', title: 'Error Validasi', text: 'Terjadi kesalahan saat validasi data SPK outstanding.' });
        return;
      }

      const payload = {
        spkNum: this.formData.spkNumber,
        spkDate: this.formData.spkDate,
        entityId: parseInt(this.formData.subcon),
        contactId: parseInt(this.formData.subconContactName),
        spkJob: parseInt(this.formData.pekerjaan),
        spkRemark: this.formData.remark,
        userId: parseInt(this.formData.gspeNama),
        status: 0,
        TblSpksubconDetails: this.detailPekerjaan.map(detail => ({
          poNum: detail.poNumber,
          poId: parseInt(detail.poId),
          sitename: detail.sitename,
          price: parseFloat(detail.price),
          startDate: detail.startDate,
          endDate: detail.endDate,
          status: 0,
          balanceinv: parseFloat(detail.price),
        })),
      };

      try {
        await axios.post(API_ENDPOINTS.saveSpk, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Data SPK Subcon berhasil disimpan!' });
        this.resetForm();
      } catch (apiError) {
        let errorMessage = 'Gagal menyimpan data SPK Subcon.';
        if (apiError.response && apiError.response.data) {
          errorMessage += ` Detail: ${apiError.response.data.detail || apiError.response.data.title || JSON.stringify(apiError.response.data)}`;
        }
        console.error('Gagal menyimpan data SPK Subcon:', apiError);
        Swal.fire({ icon: 'error', title: 'Gagal Menyimpan', text: errorMessage });
      }
    },

    openJobModal() {
      this.newJobName = '';
      this.isJobModalVisible = true;
    },

    closeJobModal() {
      this.isJobModalVisible = false;
    },

    async handleSaveNewJob() {
      if (!this.newJobName.trim()) {
        Swal.fire('Input Kosong', 'Nama job tidak boleh kosong.', 'warning');
        return;
      }
      this.isSavingJob = true;
      try {
        const response = await axios.post(API_ENDPOINTS.saveJob,
          { jobname: this.newJobName },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );

        await this.loadJobOptions();
        this.formData.pekerjaan = response.data.id;
        Swal.fire('Berhasil', `Job '${response.data.jobname}' berhasil ditambahkan.`, 'success');
        this.closeJobModal();

      } catch (error) {
        let errorMessage = 'Gagal menyimpan job baru.';
        if (error.response?.status === 409) {
          errorMessage = error.response.data;
        }
        Swal.fire('Error', errorMessage, 'error');
        console.error("Error saving new job:", error);
      } finally {
        this.isSavingJob = false;
      }
    },

    resetForm() {
      this.formData = getInitialFormData();
      this.subconInfo = { jabatan: '', noHp: '' };
      this.detailPekerjaan = [];
      this.contactOptions = [];
      this.siteOptions = [];
      this.selectedProjectData = null;
      this.loadLastSpkNumber();
    },
    exitForm() {
      this.$router.go(-1);
    },
  },

  watch: {
    'formData.subcon'(newSubconId) {
      this.formData.subconContactName = '';
      this.subconInfo = { jabatan: '', noHp: '' };
      this.loadContactOptions(newSubconId);
    },
    async 'formData.poNumber'(newPoId) {
      this.formData.siteName = '';
      this.selectedProjectData = null;

      if (newPoId) {
        const selectedPo = this.poOptions.find(po => po.id == newPoId);
        this.siteOptions = selectedPo?.sites || [];

        try {
          const response = await axios.get(API_ENDPOINTS.projectByPo(newPoId), {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          this.selectedProjectData = response.data[0] || null;
        } catch (error) {
          console.error(`Error fetching project for PO ID ${newPoId}:`, error);
        }
      } else {
        this.siteOptions = [];
      }
    },
    'formData.gspeNama'(newUserId) {
      const selectedUser = this.gspeNameOptions.find(user => user.id == newUserId);
      this.formData.gspeJabatan = selectedUser?.authorizationLevelName || '';
    },
  },
  mounted() {
    this.loadSubconOptions();
    this.loadPoOptions();
    this.loadGspeUsers();
    this.loadLastSpkNumber();
    this.loadJobOptions();
  },
};
</script>

<style scoped>
/* Styling yang ada */
.input-border {
  border-bottom: 2px solid #ccc;
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-top: none;
}

.input-border:focus {
  border-bottom-color: #007bff;
  box-shadow: none;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 5px;
  font-weight: bold;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.form-group textarea {
  resize: vertical;
}

.form-actions {
  margin-top: 20px;
}

.form-actions button {
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.form-actions button:first-child {
  background-color: #28a745;
  color: white;
  margin-right: 10px;
}

.form-actions button:last-child {
  background-color: #dc3545;
  color: white;
}

.data-grid-container {
  margin-top: 20px;
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 4px;
}

.data-grid-container h3 {
  margin-top: 0;
  margin-bottom: 10px;
}

.data-grid-container table {
  width: 100%;
  border-collapse: collapse;
}

.data-grid-container th,
.data-grid-container td {
  border: 1px solid #eee;
  padding: 8px;
  text-align: left;
}

.data-grid-container th {
  background-color: #f0f0f0;
  font-weight: bold;
}

.gspe-info {
  margin-top: 20px;
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 4px;
}

.gspe-info h3 {
  margin-top: 0;
  margin-bottom: 10px;
}

.gspe-info .form-actions {
  margin-top: 15px;
  display: flex;
  justify-content: flex-start;
  gap: 10px;
}

/* Penyesuaian untuk Vue-Select */
/* Ini penting agar gaya default input-border diterapkan ke v-select */
.v-select.input-border .vs__dropdown-toggle {
  border: none;
  /* Hilangkan border bawaan v-select karena kita pakai border dari input-border */
  padding: 0;
  /* Sesuaikan padding */
  display: flex;
  align-items: center;
  height: auto;
  /* Biarkan tinggi menyesuaikan konten */
  background-color: transparent;
  /* Pastikan background transparan */
}

/* Pastikan border bawah dari input-border tetap terlihat */
.v-select.input-border {
  border-bottom: 2px solid #ccc;
  border-radius: 0;
}

.v-select.input-border:focus-within {
  border-bottom-color: #007bff;
  box-shadow: none;
}

/* Sesuaikan input field di dalam v-select */
.v-select.input-border .vs__search,
.v-select.input-border .vs__selected {
  padding: 8px 0;
  /* Sesuaikan padding agar sama dengan input lain */
  margin: 0;
}

/* Sesuaikan placeholder jika diperlukan */
.v-select.input-border .vs__search::placeholder {
  color: #a0aec0;
  /* Warna placeholder Tailwind gray-400 */
}

/* Untuk panah dropdown */
.v-select.input-border .vs__open-indicator {
  fill: #a0aec0;
  /* Warna panah dropdown */
}

/* Untuk clear button (X) */
.v-select.input-border .vs__clear {
  fill: #a0aec0;
}

/* Styling untuk opsi dropdown */
.vs__dropdown-menu {
  border-radius: 0.5rem;
  /* Rounded xl dari Tailwind */
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  /* Shadow md dari Tailwind */
  border: 1px solid #e2e8f0;
  /* Border gray-200 */
}

/* Styling untuk opsi yang aktif (hover/fokus) */
.vs__dropdown-option--highlight {
  background: #edf2f7;
  /* Tailwind gray-100 */
  color: #2d3748;
  /* Tailwind gray-800 */
}

/* Styling untuk opsi yang sudah dipilih */
.vs__selected-options {
  display: flex;
  flex-wrap: wrap;
  padding: 0;
  margin: 0;
}
</style>